

import React, { useMemo, useEffect, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  LabelList,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  ReferenceLine,
  Label,
  Customized
} from "recharts";
import { CardContent } from "@mui/material";

import Header from "../components/Header";
import Footer from "../components/Footer";
import UraddHeader from "../components/UraddHeader";

// ---- Helpers ----
const pct = (str) =>
  str == null ? null : Number(String(str).replace("%", "").trim());

const useBreakpoints = () => {
  const get = () => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1024;
    return {
      width: w,
      isXS: w < 380,
      isSM: w < 640,
      isMD: w < 768,
      isLG: w < 1024,
      isXL: w >= 1280,
    };
  };
  const [bp, setBp] = useState(get());
  useEffect(() => {
    const onResize = () => setBp(get());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return bp;
};

const CustomTick = ({ x, y, payload }) => {
  const words = String(payload.value).split(" ");
  return (
    <text x={x} y={y + 8} textAnchor="middle" fontSize={12}>
      {words.map((word, i) => (
        <tspan x={x} dy={i === 0 ? 0 : 10} key={i}>
          {word}
        </tspan>
      ))}
    </text>
  );
};

// ---- Shared palette ----
const PALETTE = [
  "#6366F1",
  "#FF0000",
  "#22C55E",
  "#F59E0B",
  "#EC4899",
  "#0EA5E9",
  "#753500",
  "#F6EAC2",
  "#A855F7",
  "#51736E",
  "#14B8A6",
  "#F97316",
  "#84CC16",
  "#C8F527",
  "#BA73A9",
  "#C9F0CC",
  "#0F3813",
  "#FFB8B1",
  "#DFCCF1",
  "#9AB7D3",
  "#EDEAE5"
];

// ===================== DATA =====================
// missing constants & datasets
const trendData = [
  { year: 2002, prevalencePct: pct("0.75%"), ratio: "1 in 133" },
  { year: 2012, prevalencePct: pct("1.7%"), ratio: "1 in 58" },
  { year: 2018, prevalencePct: pct("2.2%") },
  { year: 2020, prevalencePct: pct("2.5%"), ratio: "1 in 40" },
  { year: 2022, prevalencePct: pct("2.7%"), ratio: "1 in 37" },
];

const marginTight = { top: 8, right: 16, left: 8, bottom: 16 };
const marginXHeavy = { top: 8, right: 16, left: 8, bottom: 32 };
const tickSmall = { fontSize: 12 };
const xHeight = 60;
const xTick = (v) => String(v);

const bySex8y = [
  { location: "Utah", female: pct("1.3%"), male: pct("4.5%") },
  { location: "U.S. Avg", female: pct("1.43%"), male: pct("4.92%") },
];
const bySex4y = [
  { location: "Utah", female: pct("0.8%"), male: pct("2.7%") },
  { location: "U.S. Avg", female: pct("1.2%"), male: pct("4.1%") },
];

const byRace = [
   { group: "White", per1k: 27.7 },
    { group: "Hispanic", per1k: 33 },
  { group: "Asian or Pacific Islander", per1k: 38.2 },

 
 
  { group: "Two or More Races", per1k: 31.9 },
   { group: "Black or African American", per1k: 36.6 },
    { group: "American Indian or Alaska Native", per1k: 37.5 },
 
]

const comp8y = [
  { name: "White", value: 67 },
  { name: "Hispanic", value: 21 },
  { name: "Asian or Pacific Islander", value: 5 },
  { name: "Two or More Races", value: 5 },
  { name: "Black or African American", value: 2 },
  { name: "American Indian or Alaska Native", value: 1 },
];
const comp4y = [
  { name: "White", value: 64 },
  { name: "Hispanic", value: 23 },
  { name: "Asian or Pacific Islander", value: 5 },
  { name: "Two or More Races", value: 5 },
  { name: "Black or African American", value: 2 },
  { name: "American Indian or Alaska Native", value: 1 },
];

const referral = [
  { outcome: "Referred for Diagnostic Evaluation", pct: pct("58.6%") },
  { outcome: "No Referral for Assessment", pct: pct("32.9%") },
  { outcome: "Referred Only to Early Intervention", pct: pct("8.6%") },
];

const ageDx = [
  { group: "Not Screened", months: 39.9 },
  { group: "Screened (Overall)", months: 36.6 },
  { group: "At-Risk, Not Referred", months: 40 },
  { group: "At-Risk, Early Intervention Only", months: 39.2 },
  { group: "At-Risk, Referred", months: 31.6 },
];

const waitTrend = [
  { year: 2020, pct: pct("33%") },
  { year: 2021, pct: pct("25%") },
  { year: 2022, pct: pct("15%") },
  { year: 2023, pct: pct("12%") },
  { year: 2024, pct: pct("10%") },
  { year: 2025, pct: pct("8%") },
];

const addm = [
  { year: 2000, sites: 6, per1k: 6.7, ratio: "1 in 150" },
  { year: 2002, sites: 14, per1k: 6.6, ratio: "1 in 150" },
  { year: 2004, sites: 8, per1k: 8, ratio: "1 in 125" },
  { year: 2006, sites: 11, per1k: 9, ratio: "1 in 110" },
  { year: 2008, sites: 14, per1k: 11.3, ratio: "1 in 88" },
  { year: 2010, sites: 11, per1k: 14.7, ratio: "1 in 68" },
  { year: 2012, sites: 11, per1k: 14.5, ratio: "1 in 69" },
  { year: 2014, sites: 11, per1k: 16.8, ratio: "1 in 59" },
  { year: 2016, sites: 11, per1k: 18.5, ratio: "1 in 54" },
  { year: 2018, sites: 11, per1k: 23, ratio: "1 in 44" },
  { year: 2020, sites: 11, per1k: 27.6, ratio: "1 in 36" },
  { year: 2022, sites: 16, per1k: 32.2, ratio: "1 in 31" },
];

const legendTop = { layout: "horizontal", verticalAlign: "top", align: "center" };
const legendBottom = { layout: "horizontal", verticalAlign: "bottom", align: "center" };


// ASD vs US & DD vs US
const utVsUsDataNew = [
  { year: "1984–88", utahASD: 0.04, usASD: 0.05, utahDD: 1.5, usDD: 1.5 },
  { year: "1990", utahASD: 0.06, usASD: 0.07, utahDD: 1.7, usDD: 1.7 },
  { year: "2000", utahASD: 0.2, usASD: 0.07, utahDD: 2.0, usDD: 1.35 },
  { year: "2006", utahASD: 0.72, usASD: 0.9, utahDD: 3.0, usDD: 1.75 },
  { year: "2010", utahASD: 1.5, usASD: 1.5, utahDD: 4.0, usDD: 2.5 },
  { year: "2014", utahASD: 2.0, usASD: 1.7, utahDD: 4.8, usDD: 2.75 },
  { year: "2016", utahASD: 2.2, usASD: 2.24, utahDD: 5.0, usDD: 3.5 },
  { year: "2018", utahASD: 2.2, usASD: 2.8, utahDD: 5.2, usDD: 3.75 },
  { year: "2020", utahASD: 2.5, usASD: 2.76, utahDD: 5.4, usDD: 3.75 },
  { year: "2022", utahASD: 2.7, usASD: 2.8, utahDD: 5.6, usDD: 4.0 },
  { year: "2025 ", utahASD: 2.7, usASD: 3.2, utahDD: 5.8, usDD: 4.5 },
];
const utVsUsData = [
  { label: "2012 (8yrs)", utah: pct("1.7%"), us: pct("1.14%") },
  { label: "2018 (8yrs)", utah: pct("2.2%"), us: pct("2.3%") },
  { label: "2020 (4yrs)", utah: pct("1.3%"), us: pct("2.9%") },
  { label: "2020 (8yrs)", utah: pct("2.5%"), us: pct("2.3%") },
  { label: "2022 (4yrs)", utah: pct("1.8%"), us: pct("2.9%") },
  { label: "2022 (8yrs)", utah: pct("2.7%"), us: pct("3.2%") },
];

// Utah detail
const utahDetail = [
  { year: "1984–88", asdPct: 0.04, per1k: 0.4, ddPct: 1.5 },
  { year: "1990", asdPct: 0.06, per1k: 0.6, ddPct: 1.7 },
  { year: "2000", asdPct: 0.2, per1k: 2.0, ddPct: 2.0 },
  { year: "2002", asdPct: 0.3, per1k: 3.0, ddPct: 2.5 },
  { year: "2004", asdPct: 0.55, per1k: 5.5, ddPct: 2.8 },
  { year: "2006", asdPct: 0.72, per1k: 7.2, ddPct: 3.0 },
  { year: "2008", asdPct: 1.1, per1k: 11, ddPct: 3.5 },
  { year: "2010", asdPct: 1.5, per1k: 15, ddPct: 4.0 },
  { year: "2012", asdPct: 2.2, per1k: 22, ddPct: 4.5 },
  { year: "2013", asdPct: 2.2, per1k: 21, ddPct: 4.5 },
  { year: "2014", asdPct: 2.0, per1k: 20, ddPct: 4.8 },
  { year: "2016", asdPct: 2.2, per1k: 22, ddPct: 5.0 },
  { year: "2018", asdPct: 2.2, per1k: 22, ddPct: 5.2 },
  { year: "2020", asdPct: 2.5, per1k: 25, ddPct: 5.4 },
  { year: "2022", asdPct: 2.7, per1k: 27, ddPct: 5.6 },
  { year: "2025 ", asdPct: 2.7, per1k: 27, ddPct: 5.8 },

];

// const utahDetailClean = utahDetail.map(d => {
//   let yearStr = String(d.year).trim();

//   if (yearStr.includes("–")) {
//     // Take midpoint of a range, e.g. "1984–88" → 1986
//     const [start, end] = yearStr.split("–").map(n => parseInt(n, 10));
//     return { ...d, year: Math.round((start + end) / 2) };
//   }

//   return { ...d, year: parseInt(yearStr, 10) };
// });
// const utahDetailClean = utahDetail.map(d => {
//   let yearStr = String(d.year).trim();

//   // Normalize en dash or hyphen to a plain dash
//   yearStr = yearStr.replace(/[–—-]/g, "-");

//   if (yearStr.includes("-")) {
//     const [start, end] = yearStr.split("-").map(n => parseInt(n.trim(), 10));
//     return { ...d, year: Math.round((start + end) / 2) }; // midpoint
//   }

//   return { ...d, year: parseInt(yearStr, 10) };
// });

const utahDetailClean = utahDetail.map(d => {
  let yearStr = String(d.year).trim();

  // Match a range like "1984–88" or "1984-88"
  const rangeMatch = yearStr.match(/^(\d{4})\D+(\d{2,4})$/);
  if (rangeMatch) {
    const start = parseInt(rangeMatch[1], 10);
    const end =
      rangeMatch[2].length === 2
        ? parseInt(rangeMatch[1].slice(0, 2) + rangeMatch[2], 10) // "88" → "1988"
        : parseInt(rangeMatch[2], 10);

    return { ...d, year: Math.round((start + end) / 2) };
  }

  // Fallback: plain year like "2000" or "2025 "
  const yearNum = parseInt(yearStr, 10);
  return { ...d, year: isNaN(yearNum) ? null : yearNum };
}).filter(d => d.year !== null);




// ---- Card ----
const Card = ({ title, subtitle, children }) => (
  <div className="bg-white/80 backdrop-blur rounded-2xl shadow p-5 md:p-6 border border-slate-200">
    <div className="mb-4">
      <h2 className="text-lg md:text-xl font-semibold text-slate-800">{title}</h2>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const sources = [
  {
    title: "North Carolina Society of Autism",
    url: "https://www.ncsautism.org/",
    source: "ncsautism.org",
    description: "Resources, programs, and research insights on autism in North Carolina."
  },
  {
    title: "Trends in Autism Spectrum Disorder Prevalence",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC4467195/",
    source: "PMC NCBI",
    description: "Peer-reviewed article on autism prevalence trends and methodologies."
  },
  {
    title: "CDC MMWR Report on Autism (2025)",
    url: "https://www.cdc.gov/mmwr/volumes/74/ss/ss7402a1.htm",
    source: "CDC.gov",
    description: "CDC’s 2025 Surveillance Summary on autism prevalence in the U.S."
  },
  {
    title: "Utah-Specific Autism Prevalence Report",
    url: "https://www.cdc.gov/ncbddd/autism/pdf/Utah.pdf",
    source: "CDC.gov",
    description: "CDC’s state-level report on autism prevalence in Utah."
  },
  {
    title: "Utah Autism Study Press Release",
    url: "https://healthcare.utah.edu/press-releases/2025/04/autism-prevalence-utah-remains-steady-lower-nation-report",
    source: "Healthcare.utah.edu",
    description: "Press release: Autism prevalence in Utah remains steady but below national average."
  }
];

export default function Urrad() {
  const bp = useBreakpoints();
  const chartH = bp.isSM ? 260 : 400;
  const barSize = bp.isSM ? 14 : 22;
   const pieOuter = bp.isSM ? 68 : 90;
  const pieInner = bp.isSM ? 40 : 56;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 text-slate-800">
      <UraddHeader />
      <main className="mx-auto w-full mt-[14rem] max-w-7xl px-4 sm:px-6 md:px-8 space-y-8">
        <div>The Utah Registry of Autism and Developmental Disabilities (URADD) primarily focuses on Autism Spectrum Disorder (ASD) and other developmental disabilities. URADD operates within 
          Utah's broader developmental disabilities framework which recognizes multiple disability categories for surveillance and service planning.
          </div>
        <section>
          <h1 className="text-2xl md:text-3xl font-bold">
            Autism Spectrum Disorder & Developmental Disabilities (ASD & DD) — Utah Insights
          </h1>
          <p className="text-slate-600 mt-1">
            Combined prevalence visualizations for Utah vs U.S., plus URADD detail data.
          </p>
        </section>



              <Card title="ASD Prevalence Trend in Utah" subtitle="8-year-old children (2002–2022)">
   <div style={{ height: chartH }}>
     {/* 👇 scrollable wrapper for small screens */}
     <div className="overflow-x-auto">
       <div className="min-w-[600px] sm:min-w-full">
       

         <ResponsiveContainer width="100%" height={chartH}>
   <LineChart data={trendData} margin={marginTight}>
     <CartesianGrid strokeDasharray="3 3" />

     {/* X Axis with label */}
     <XAxis dataKey="year" tick={tickSmall}>
       <Label value="Year" offset={-5} position="insideBottom" />
     </XAxis>

     {/* Y Axis with label */}
    <YAxis unit="%" tick={tickSmall} allowDecimals={false}>
       <Label
        value="Prevalence (%)"
        angle={-90}
        position="insideLeft"
        style={{ textAnchor: "middle" }}
      />
    </YAxis>

    <Tooltip
      wrapperStyle={{ fontSize: tickSmall.fontSize }}
      formatter={(v) => `${v}%`}
    />

    <Legend
      verticalAlign="top"
      align="center"
      wrapperStyle={{ fontSize: tickSmall.fontSize }}
    />

    <Line
      dataKey="prevalencePct"
      name="Utah"
      stroke={PALETTE[1]}
      strokeWidth={3}
      dot={{ r: bp.isSM ? 2 : 4 }}
      activeDot={{ r: bp.isSM ? 4 : 6 }}
    />
    {!bp.isSM && <LabelList dataKey="ratio" position="top" />}
  </LineChart>
</ResponsiveContainer>

      </div>
    </div>
  </div>
  <div className="mt-4 text-sm text-gray-700 leading-relaxed">
    <strong>Key Insight:</strong> Utah has experienced a significant increase in autism prevalence among 8-year-old children over the past two decades, with rates rising from 0.75% in 2002 to 2.7% in 2022. This represents a 3.6x increase, reflecting improved diagnosis, greater awareness, and potentially changing environmental factors.
  </div>
</Card>



<Card title="Utah vs National ASD Prevalence" subtitle="Across years and age groups">
  <div style={{ height: chartH }}>
    {/* 👇 scrollable wrapper */}
    <div className="overflow-x-auto">
      <div className="min-w-[700px] sm:min-w-full">
        <ResponsiveContainer width="100%" height={chartH}>
          <BarChart
            data={utVsUsData}
            margin={marginXHeavy}
            barCategoryGap={bp.isSM ? "30%" : "15%"}
          >
            <CartesianGrid strokeDasharray="3 3" />

            {/* X Axis with label */}
            <XAxis
              dataKey="label"
              height={xHeight}
              tick={tickSmall}
              tickFormatter={xTick}
              interval={bp.isSM ? "preserveStartEnd" : 0}
            >
              <Label value="Year / Age Group" offset={-5} position="insideBottom" />
            </XAxis>

            {/* Y Axis with label */}
            <YAxis unit="%" tick={tickSmall} allowDecimals={false}>
              <Label
                value="Prevalence (%)"
                angle={-90}
                position="insideLeft"
                style={{ textAnchor: "middle" }}
              />
            </YAxis>

            <Tooltip
              wrapperStyle={{ fontSize: tickSmall.fontSize }}
              formatter={(v) => `${v}%`}
            />

            <Legend
              verticalAlign="top"
              align="center"
              wrapperStyle={{ fontSize: tickSmall.fontSize }}
            />

            <Bar dataKey="utah" name="Utah" barSize={barSize} fill={PALETTE[1]} />
            <Bar dataKey="us" name="U.S." barSize={barSize} fill={PALETTE[2]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  </div>

  {/* 👇 Key Insight section BELOW chart */}
  <div className="mt-4 text-sm text-gray-700 leading-relaxed">
    <strong>Key Insight:</strong> Utah's autism prevalence rates show notable
    differences compared to national averages. In the year 2022 for 8-year-olds, Utah's rate
    (2.7%) is below the national average (3.2%), while for 4-year-olds, Utah's
    prevalence (1.8%) is significantly lower than the national average (2.9%).
  </div>
</Card>

      
      {/* Key Insight: The female-to-male ratio of 1:3.4 is consistent across both age groups in Utah and matches the national average, suggesting a stable gender pattern in ASD diagnosis. */}



        {/* Sex comparisons */}
        <div className="border p-8 bg-white rounded-lg shadow-lg">
        <section className="grid grid-cols-1  lg:grid-cols-2 gap-6">
       

          <Card title="By Sex (8yrs, 2022)">
  <div style={{ height: chartH }}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={bySex8y}
        margin={marginTight}
        barCategoryGap={bp.isSM ? "40%" : "20%"}
      >
        <CartesianGrid strokeDasharray="3 3" />

        {/* X Axis with label */}
        <XAxis dataKey="location" tick={tickSmall}>
          <Label value="Location" offset={-5} position="insideBottom" />
        </XAxis>

        {/* Y Axis with label */}
        <YAxis unit="%" tick={tickSmall} allowDecimals={false}>
          <Label
            value="Prevalence (%)"
            angle={-90}
            position="insideLeft"
            style={{ textAnchor: "middle" }}
          />
        </YAxis>

        <Tooltip
          wrapperStyle={{ fontSize: tickSmall.fontSize }}
          formatter={(v) => `${v}%`}
        />

        {/* ✅ Legend for Boys vs Girls */}
        <Legend
          verticalAlign="top"
          align="center"
          wrapperStyle={{ fontSize: tickSmall.fontSize }}
        />

        <Bar
          dataKey="female"
          name="Girls"
          barSize={barSize}
          fill={PALETTE[4]}
        />
        <Bar
          dataKey="male"
          name="Boys"
          barSize={barSize}
          fill={PALETTE[5]}
        />
      </BarChart>
    </ResponsiveContainer>
  </div>
</Card>

<Card title="By Sex (4yrs, 2022)">
  <div style={{ height: chartH }}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={bySex4y}
        margin={marginTight}
        barCategoryGap={bp.isSM ? "40%" : "20%"}
      >
        <CartesianGrid strokeDasharray="3 3" />

        {/* X Axis with label */}
        <XAxis dataKey="location" tick={tickSmall}>
          <Label value="Location" offset={-5} position="insideBottom" />
        </XAxis>

        {/* Y Axis with label */}
        <YAxis unit="%" tick={tickSmall} allowDecimals={false}>
          <Label
            value="Prevalence (%)"
            angle={-90}
            position="insideLeft"
            style={{ textAnchor: "middle" }}
          />
        </YAxis>

        <Tooltip
          wrapperStyle={{ fontSize: tickSmall.fontSize }}
          formatter={(v) => `${v}%`}
        />

        {/* ✅ Legend for Boys vs Girls */}
        <Legend
          verticalAlign="top"
          align="center"
          wrapperStyle={{ fontSize: tickSmall.fontSize }}
        />

        <Bar
          dataKey="female"
          name="Girls"
          barSize={barSize}
          fill={PALETTE[4]}
        />
        <Bar
          dataKey="male"
          name="Boys"
          barSize={barSize}
          fill={PALETTE[5]}
        />
      </BarChart>
    </ResponsiveContainer>
  </div>
  
</Card>



        </section>
        <div className="mt-4 text-sm text-gray-700 ">
    <strong>Key Insight:</strong> The girls-to-boys ratio of 1:3.4 is consistent across both age groups in Utah and matches the national average, suggesting a stable gender pattern in ASD diagnosis.
  </div>
        </div>

        {/* Race & Ethnicity */}

        <div className="border p-8 bg-white rounded-lg shadow-lg">
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
         

       


<Card title="ASD Prevalence Rate by Race/Ethnicity-2022">
  <div style={{ height: chartH }}>
    {/* 👇 scrollable wrapper */}
    <div className="overflow-x-auto">
      <div className="min-w-[700px] sm:min-w-full">
        <ResponsiveContainer width="100%" height={chartH}>
          <BarChart data={byRace} margin={marginTight}>
            <CartesianGrid strokeDasharray="3 3" />

            {/* X Axis with label */}
            <XAxis
              type="category"
              dataKey="group"
              interval={0}
              height={xHeight}
              tick={<CustomTick />}
            >
              <Label value="Race / Ethnicity" offset={-5} position="insideBottom" />
            </XAxis>

            {/* Y Axis with label */}
            <YAxis
              type="number"
              tick={tickSmall}
              domain={[0, (max) => Math.ceil(Math.max(40, max))]}
            >
              <Label
                value="Prevalence (per 1,000)"
                angle={-90}
                position="insideLeft"
                style={{ textAnchor: "middle" }}
              />
            </YAxis>

            <Tooltip
              wrapperStyle={{ fontSize: tickSmall.fontSize }}
              formatter={(v) => `${v} per 1k`}
            />

            {/* ✅ Legend with descriptive label */}
            {/* <Legend
              verticalAlign="top"
              align="center"
              wrapperStyle={{ fontSize: tickSmall.fontSize }}
            /> */}

         
<Bar
  dataKey="per1k"
  name="ASD Prevalence"
  barSize={bp.isSM ? 14 : 18}
>
  {comp8y.map((_, i) => (
    <Cell key={i} fill={PALETTE[i+6]} />
  ))}
</Bar>


          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  </div>
</Card>




          <Card title="ASD Prevalence % by Race/Ethnicity-2022">
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {/* 8 year old children */}
    <div>
      <h5 className="text-center font-semibold mb-2">8-Year-Old Children</h5>
      <div style={{ height: chartH }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={comp8y}
              dataKey="value"
              nameKey="name"
              innerRadius={pieInner}
              outerRadius={pieOuter}
              paddingAngle={bp.isSM ? 1 : 2}
            >
              {comp8y.map((_, i) => (
                <Cell key={i} fill={PALETTE[i+6]} />
              ))}
            </Pie>
            <Tooltip
              wrapperStyle={{ fontSize: tickSmall.fontSize }}
              formatter={(v) => `${v}%`}
            />
            {/* <Legend {...legendBottom} /> */}
            <Legend
  {...legendBottom}
  wrapperStyle={{ fontSize: "11px" }}   // legend text smaller
  iconSize={8}                          // legend icon smaller
  layout="horizontal"
  align="center"
  verticalAlign="bottom"
/>

          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>

    {/* 4 year old children */}
    <div>
      <h5 className="text-center font-semibold mb-2">4-Year-Old Children</h5>
      <div style={{ height: chartH }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={comp4y}
              dataKey="value"
              nameKey="name"
              innerRadius={pieInner}
              outerRadius={pieOuter}
              paddingAngle={bp.isSM ? 1 : 2}
            >
              {comp4y.map((_, i) => (
                <Cell key={i} fill={PALETTE[(i) % PALETTE.length+6]} />
              ))}
            </Pie>
            <Tooltip
              wrapperStyle={{ fontSize: tickSmall.fontSize }}
              formatter={(v) => `${v}%`}
            />
            {/* <Legend {...legendBottom} /> */}
            <Legend
  {...legendBottom}
  wrapperStyle={{ fontSize: "11px" }}   // legend text smaller
  iconSize={8}                          // legend icon smaller
  layout="horizontal"
  align="center"
  verticalAlign="bottom"
/>

          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  </div>
</Card>

        </section>

         <div className="mt-4 text-sm text-gray-700 ">
    <strong>Key Insight:</strong> Utah's data reveals significant disparities in autism prevalence across racial and ethnic groups. While Asian or Pacific Islander children have the highest prevalence rate at 38.2 per 1,000 children (1 in 26), White children show a lower prevalence rate of 27.7 per 1,000 (1 in 36).
However, White children represent the largest percentage of total ASD cases due to population demographics.

  </div>

        </div>

        {/* Screening & Age at Dx */}
          <div className="border p-8 bg-white rounded-lg shadow-lg">
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        

         

<Card title="Early Screening & Referral-2022">
  <div style={{ height: chartH }}>
    <div className="overflow-x-auto">
      <div className="min-w-[700px] sm:min-w-full">
        <ResponsiveContainer width="100%" height={chartH}>
          <BarChart
            data={referral}
            margin={marginXHeavy}
            barCategoryGap={bp.isSM ? "30%" : "15%"}
          >
            <CartesianGrid strokeDasharray="3 3" />

            {/* X Axis with label */}
            <XAxis
              dataKey="outcome"
              height={xHeight}
              tick={<CustomTick />}
              tickFormatter={xTick}
              interval={0}
            >
              <Label
                value="Screening and Referral "
                offset={-5}
                position="insideBottom"
              />
            </XAxis>

            {/* Y Axis with label */}
            <YAxis unit="%" tick={tickSmall} allowDecimals={false}>
              <Label
                value="Percent (%)"
                angle={-90}
                position="insideLeft"
                style={{ textAnchor: "middle" }}
              />
            </YAxis>

            <Tooltip
              wrapperStyle={{ fontSize: tickSmall.fontSize }}
              formatter={(v) => `${v}%`}
            />

            {/* ✅ Legend with a descriptive name */}
            {/* <Legend
              verticalAlign="top"
              align="center"
              wrapperStyle={{ fontSize: tickSmall.fontSize }}
            /> */}

            <Bar dataKey="pct" name="Percent" barSize={bp.isSM ? 18 : 26}>
              {referral.map((_, i) => (
                <Cell key={i} fill={PALETTE[i % PALETTE.length+10]} />
              ))}
              {!bp.isSM && (
                <LabelList
                  dataKey="pct"
                  position="top"
                  formatter={(v) => `${v}%`}
                />
              )}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  </div>
</Card>



        

      

<Card title="Average Age at Diagnosis-2022" subtitle="Lower months = earlier diagnosis">
  <div style={{ height: chartH }}>
    {/* 👇 Scrollable wrapper */}
    <div className="overflow-x-auto">
      <div className="min-w-[700px] sm:min-w-full">
        <ResponsiveContainer width="100%" height={chartH}>
          <BarChart
            data={ageDx}
            margin={marginXHeavy}
            barCategoryGap={bp.isSM ? "30%" : "15%"}
          >
            <CartesianGrid strokeDasharray="3 3" />

            {/* X Axis with label */}
            <XAxis
              dataKey="group"
              height={xHeight}
              tick={<CustomTick />}
              tickFormatter={xTick}
              interval={0} // 👈 ensures every label shows
            >
              <Label
                value="Group"
                offset={-5}
                position="insideBottom"
              />
            </XAxis>

            {/* Y Axis with label */}
            <YAxis
              unit=" mo"
              tick={tickSmall}
              allowDecimals={false}
              domain={[0, 48]}   // fixed range
              ticks={[0, 12, 24, 36, 48]} // 👈 custom tick values
            >
              <Label
                value="Average Age at Diagnosis (months)"
                angle={-90}
                position="insideLeft"
                style={{ textAnchor: "middle" }}
              />
            </YAxis>

            <Tooltip
              wrapperStyle={{ fontSize: tickSmall.fontSize }}
              formatter={(v) => `${v} months`}
            />

            {/* ✅ Legend clarified */}
            {/* <Legend
              verticalAlign="top"
              align="center"
              wrapperStyle={{ fontSize: tickSmall.fontSize }}
            /> */}

            {/* <Bar
              dataKey="months"
              name="Average Age (months)"
              barSize={bp.isSM ? 18 : 26}
              fill={PALETTE[7]}
            >
              {!bp.isSM && <LabelList dataKey="months" position="top" />}
            </Bar> */}
            <Bar
  dataKey="months"
  name="Average Age (months)"
  barSize={bp.isSM ? 18 : 26}
>
  {ageDx.map((entry, index) => (
    <Cell key={`cell-${index}`} fill={PALETTE[index % PALETTE.length+14]} />
  ))}
  {!bp.isSM && <LabelList dataKey="months" position="top" />}
</Bar>

          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  </div>
</Card>


        </section>

      <div className="mt-4 text-sm text-gray-700 ">
    <strong>Key Insight:</strong> Children who are screened, identified as "at-risk," and promptly referred for diagnostic evaluation receive their diagnosis 8.4 months earlier than those who are not referred (31.6 months vs 40.0 months).


  </div>
</div>

<Card title="Diagnostic Evaluation Wait Time (2020–2025)" subtitle="% of 4-year-olds waiting in Utah">
  <span className="text-green-700">DSM-V released in 2013</span>
  <div style={{ height: chartH }} className="overflow-x-auto">
    <div className="min-w-[700px]"> {/* 👈 ensures chart stays wide enough */}
      <ResponsiveContainer width="100%" height={chartH}>
        <AreaChart data={waitTrend} margin={marginTight}>
          <defs>
            <linearGradient id="gradWait" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={PALETTE[10]} stopOpacity={0.5} />
              <stop offset="100%" stopColor={PALETTE[10]} stopOpacity={0.06} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" />

          {/* X Axis with label */}
          <XAxis dataKey="year" tick={tickSmall} interval={0}>
            <Label value="Year" offset={-5} position="insideBottom" />
          </XAxis>

          {/* Y Axis with label */}
          <YAxis unit="%" tick={tickSmall} allowDecimals={false} domain={[0, 40]}>
            <Label
              value="Percent of 4-year-olds Waiting"
              angle={-90}
              position="insideLeft"
              style={{ textAnchor: "middle" }}
            />
          </YAxis>

          <Tooltip
            wrapperStyle={{ fontSize: tickSmall.fontSize }}
            formatter={(v) => `${v}%`}
          />

          {/* ✅ Legend */}
          <Legend
            verticalAlign="top"
            align="center"
            wrapperStyle={{ fontSize: tickSmall.fontSize }}
          />

          {/* DSM-V Highlight */}
          <ReferenceLine x={2022} stroke="green" strokeDasharray="3 3">
            <Label
              value="DSM-V revised (2022)"
              angle={0}
              position="insideTop"
              fill="green"
            />
          </ReferenceLine>

          <Area
            type="monotone"
            dataKey="pct"
            name="Children Waiting"
            stroke={PALETTE[10]}
            fill="url(#gradWait)"
            strokeWidth={3}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
   <div className="mt-4 text-sm text-gray-700 ">
    <strong>Key Insight:</strong> Utah has made significant progress in reducing the percentage of 4-year-olds waiting for autism diagnostic evaluation. From 2020 to 2025, the percentage is projected to decrease from 33% to 8%, representing a 76% reduction in wait times over this five-year period.


  </div>
</Card>




      
     

<Card title="URADD in Context: ADDM Network (2000–2022)" subtitle="Combined prevalence per 1,000">
  {/* 👇 scrollable wrapper */}
  <div style={{ height: chartH }} className="overflow-x-auto">
    <div className="min-w-[900px]"> {/* 👈 ensures chart stays wide enough */}
      <ResponsiveContainer width="100%" height={chartH}>
        <LineChart data={addm} margin={marginTight}>
          <CartesianGrid strokeDasharray="3 3" />
          
          <XAxis
            dataKey="year"
            type="number"
            domain={[2000, 2022]}
            ticks={Array.from({ length: 23 }, (_, i) => 2000 + i)} // 2000–2022 inclusive
            tick={tickSmall}
            interval={0} // 👈 force all years to show
            label={{
              value: "Year",
              position: "insideBottom",
              offset: -5,
            }}
          />

          {/* ✅ Only left axis */}
          <YAxis
            yAxisId="left"
            orientation="left"
            tick={tickSmall}
            label={{ value: "Per 1,000", angle: -90, position: "insideLeft" }}
          />

          <Tooltip
            wrapperStyle={{ fontSize: tickSmall.fontSize }}
            formatter={(v, n) => `${v} per 1,000`}
          />
          <Legend {...legendTop} />

          {/* Line only for left axis */}
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="per1k"
            name="Per 1,000"
            stroke={PALETTE[0]}
            strokeWidth={3}
            dot={{ r: bp.isSM ? 2 : 3 }}
          />

          {/* DSM-V highlight */}
          <ReferenceLine
            x={2013}
            yAxisId="left"
            stroke="green"
            strokeDasharray="4 4"
            ifOverflow="visible"
          >
            <Label
              value="DSM-V (2013)"
              position="center"
              fill="green"
              fontSize={12}
            />
          </ReferenceLine>

          {/* Ratio labels */}
          {!bp.isSM && (
            <LabelList
              dataKey="ratio"
              position="top"
              content={({ x, y, value }) => {
                if (!value) return null;
                return (
                  <text
                    x={x}
                    y={y - 8}
                    textAnchor="middle"
                    fontSize={11}
                    fill="#64748B"
                  >
                    {value}
                  </text>
                );
              }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
  <div className="mt-4 text-sm text-gray-700 ">
    <strong>Key Insight:</strong> The Utah Registry of Autism and Developmental Disabilities (URADD) contributes to the CDC's Autism and Developmental Disabilities Monitoring (ADDM) Network, which tracks autism prevalence across multiple sites in the United States. This collaborative effort has documented a significant increase in autism prevalence over the past two decades.



  </div>
</Card>



        {/* ========== ASD vs US, DD vs US ========== */}
        {/* <div>ASD & DD Data from other sources since 1984</div> */}

         <section>
          <h1 className="text-2xl md:text-3xl font-bold">
            ASD & DD Data from other sources since 1984
          </h1>
          {/* <p className="text-slate-600 mt-1">
            Combined prevalence visualizations for Utah vs U.S., plus URADD detail data.
          </p> */}
        </section>

       
        {/* <Card title="ASD Prevalence: Utah vs U.S. (1984–2025)">
  <ResponsiveContainer width="100%" height={chartH}>
    <LineChart data={utVsUsDataNew}>
      <CartesianGrid strokeDasharray="3 3" />

      <XAxis 
        dataKey="year" 
        label={{ value: "Year", position: "insideBottom", offset: -5 }}
      />

      <YAxis 
        unit="%" 
        label={{ value: "Prevalence (%)", angle: -90, position: "insideLeft" }}
      />

      <Tooltip formatter={(v) => `${v}%`} />
      <Legend />

      <Line dataKey="utahASD" name="Utah ASD" stroke={PALETTE[1]} strokeWidth={3} />
      <Line dataKey="usASD" name="U.S. ASD" stroke={PALETTE[2]} strokeWidth={3} />
    </LineChart>
  </ResponsiveContainer>

   <div className="mt-4 text-sm text-gray-700 ">
    <strong>Key Insight:</strong> The values showing Autism Spectrum Disorder (ASD) prevalence trends for both Utah and the United States (national averages), from the 1980s through 2025 are synthesized from established CDC summaries, systematic reviews, and epidemiological reports.


  </div>
</Card> */}

<Card title="ASD Prevalence: Utah vs U.S. (1984–2025)">
  {/* 👇 scrollable wrapper */}
  <div className="overflow-x-auto">
    <div className="min-w-[700px] sm:min-w-full">
      <ResponsiveContainer width="100%" height={chartH}>
        <LineChart data={utVsUsDataNew}>
          <CartesianGrid strokeDasharray="3 3" />

          <XAxis
            dataKey="year"
            label={{ value: "Year", position: "insideBottom", offset: -5 }}
          />

          <YAxis
            unit="%"
            label={{ value: "Prevalence (%)", angle: -90, position: "insideLeft" }}
          />

          <Tooltip formatter={(v) => `${v}%`} />
          <Legend />

          <Line
            dataKey="utahASD"
            name="Utah ASD"
            stroke={PALETTE[1]}
            strokeWidth={3}
          />
          <Line
            dataKey="usASD"
            name="U.S. ASD"
            stroke={PALETTE[2]}
            strokeWidth={3}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>

  <div className="mt-4 text-sm text-gray-700">
    <strong>Key Insight:</strong> The values showing Autism Spectrum Disorder
    (ASD) prevalence trends for both Utah and the United States (national
    averages), from the 1980s through 2025 are synthesized from established CDC
    summaries, systematic reviews, and epidemiological reports.
  </div>
</Card>

    

        {/* <Card title="DD Prevalence: Utah vs U.S. (1984–2025)">
  <ResponsiveContainer width="100%" height={chartH}>
    <LineChart data={utVsUsDataNew}>
      <CartesianGrid strokeDasharray="3 3" />

      <XAxis 
        dataKey="year" 
        label={{ value: "Year", position: "insideBottom", offset: -5 }}
      />

      <YAxis 
        unit="%" 
        label={{ value: "Prevalence (%)", angle: -90, position: "insideLeft" }}
      />

      <Tooltip formatter={(v) => `${v}%`} />
      <Legend />

      <Line dataKey="utahDD" name="Utah DD" stroke={PALETTE[1]} strokeWidth={3} />
      <Line dataKey="usDD" name="U.S. DD" stroke={PALETTE[2]} strokeWidth={3} />
    </LineChart>
  </ResponsiveContainer>

   <div className="mt-4 text-sm text-gray-700 ">
    <strong>Key Insight:</strong> Utah’s DD prevalence has steadily risen since the early 2000s, outpacing national levels. By 2025, Utah is projected at nearly 5.8%, compared to the U.S. at 4.5%, reflecting a persistent gap.


  </div>
</Card> */}


<Card title="DD Prevalence: Utah vs U.S. (1984–2025)">
  {/* 👇 scrollable wrapper */}
  <div className="overflow-x-auto">
    <div className="min-w-[700px] sm:min-w-full">
      <ResponsiveContainer width="100%" height={chartH}>
        <LineChart data={utVsUsDataNew}>
          <CartesianGrid strokeDasharray="3 3" />

          <XAxis
            dataKey="year"
            label={{ value: "Year", position: "insideBottom", offset: -5 }}
          />

          <YAxis
            unit="%"
            label={{ value: "Prevalence (%)", angle: -90, position: "insideLeft" }}
          />

          <Tooltip formatter={(v) => `${v}%`} />
          <Legend />

          <Line
            dataKey="utahDD"
            name="Utah DD"
            stroke={PALETTE[1]}
            strokeWidth={3}
          />
          <Line
            dataKey="usDD"
            name="U.S. DD"
            stroke={PALETTE[2]}
            strokeWidth={3}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>

  <div className="mt-4 text-sm text-gray-700">
    <strong>Key Insight:</strong> Utah’s DD prevalence has steadily risen since
    the early 2000s, outpacing national levels. By 2025, Utah is projected at
    nearly 5.8%, compared to the U.S. at 4.5%, reflecting a persistent gap.
  </div>
</Card>


  



        {/* <Card title="Utah Prevalence Details (ASD %, DD %)">
  <ResponsiveContainer width="100%" height={chartH}>
    <BarChart data={utahDetail} barCategoryGap="20%">
      <CartesianGrid strokeDasharray="3 3" />

      <XAxis 
        dataKey="year" 
        label={{ value: "Year", position: "insideBottom", offset: -5 }}
      />

      <YAxis 
        label={{ value: "Prevalence (%)", angle: -90, position: "insideLeft" }}
      />

      <Tooltip />
      <Legend />

      <Bar dataKey="asdPct" name="ASD (%)" fill={PALETTE[0]} barSize={barSize}>
        <LabelList dataKey="asdPct" position="top" formatter={(v) => `${v}%`} />
      </Bar>
      <Bar dataKey="ddPct" name="DD (%)" fill={PALETTE[3]} barSize={barSize}>
        <LabelList dataKey="ddPct" position="top" formatter={(v) => `${v}%`} />
      </Bar>
    </BarChart>
  </ResponsiveContainer>
    <div className="mt-4 text-sm text-gray-700 ">
    <strong>Key Insight:</strong> Both ASD and DD prevalence in Utah have steadily increased since the 2000s. While DD rates remain consistently higher, ASD prevalence has shown sharper growth, narrowing the gap over time.


  </div>
</Card> */}

<Card title="Utah Prevalence Details (ASD %, DD %)">
  {/* 👇 scrollable wrapper */}
  <div className="overflow-x-auto">
    <div className="min-w-[700px] sm:min-w-full">
      <ResponsiveContainer width="100%" height={chartH}>
        <BarChart data={utahDetail} barCategoryGap="20%">
          <CartesianGrid strokeDasharray="3 3" />

          <XAxis
            dataKey="year"
            label={{ value: "Year", position: "insideBottom", offset: -5 }}
          />

          <YAxis
            label={{ value: "Prevalence (%)", angle: -90, position: "insideLeft" }}
          />

          <Tooltip />
          <Legend />

          <Bar dataKey="asdPct" name="ASD (%)" fill={PALETTE[0]} barSize={barSize}>
            <LabelList
              dataKey="asdPct"
              position="top"
              formatter={(v) => `${v}%`}
            />
          </Bar>
          <Bar dataKey="ddPct" name="DD (%)" fill={PALETTE[3]} barSize={barSize}>
            <LabelList
              dataKey="ddPct"
              position="top"
              formatter={(v) => `${v}%`}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>

  <div className="mt-4 text-sm text-gray-700">
    <strong>Key Insight:</strong> Both ASD and DD prevalence in Utah have steadily
    increased since the 2000s. While DD rates remain consistently higher, ASD
    prevalence has shown sharper growth, narrowing the gap over time.
  </div>
</Card>



      

{/* 
<Card title="ASD Prevalence in Utah (per 1,000)" >
  <ResponsiveContainer width="100%" height={chartH}>
    
    <LineChart
  data={utahDetail}
  margin={{ left: 20, right: 20, bottom: 40, top: 20 }}   // 👈 adjust
>

  <CartesianGrid strokeDasharray="3 3" />


  <XAxis
  dataKey="year"
  type="category"
  interval={0}
  tick={{ fontSize: 15 }}   // 👈 smaller text
  label={{ value: "Year", position: "insideBottom", offset: -4 }}
/>


  <YAxis
    label={{ value: "ASD per 1,000", angle: -90, position: "insideLeft" }}
  />

  <Tooltip formatter={(v) => `${v} per 1,000`} />
  <Legend />

  <Line
    dataKey="per1k"
    name="ASD (per 1,000)"
    stroke="blue"
    strokeWidth={3}
    dot={{ r: 4 }}
  />

 
  <ReferenceLine
  x="2013"
  stroke="green"
  strokeDasharray="4 4"
  ifOverflow="visible"
>
  <Label
    value="DSM-V (2013)"
    position="insideTop"   // 👈 places text at the top of the line
    fill="green"
    fontSize={12}
  />
</ReferenceLine>

</LineChart>

  </ResponsiveContainer>
  <div className="mt-4 text-sm text-gray-700 ">
    <strong>Key Insight:</strong> ASD prevalence in Utah has risen sharply from fewer than 1 per 1,000 in the late 1980s to nearly 27 per 1,000 by 2025. The 2013 DSM-V diagnostic changes coincide with a leveling trend, followed by continued gradual growth.


  </div>
</Card> */}

<Card title="ASD Prevalence in Utah (per 1,000)">
  {/* 👇 scroll wrapper */}
  <div className="overflow-x-auto">
    <div className="min-w-[700px] sm:min-w-full">
      <ResponsiveContainer width="100%" height={chartH}>
        <LineChart
          data={utahDetail}
          margin={{ left: 20, right: 20, bottom: 40, top: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" />

          <XAxis
            dataKey="year"
            type="category"
            interval={0}
            tick={{ fontSize: 12 }}   // 👈 smaller text for mobile
            label={{ value: "Year", position: "insideBottom", offset: -4 }}
          />

          <YAxis
            label={{ value: "ASD per 1,000", angle: -90, position: "insideLeft" }}
          />

          <Tooltip formatter={(v) => `${v} per 1,000`} />
          <Legend />

          <Line
            dataKey="per1k"
            name="ASD (per 1,000)"
            stroke="blue"
            strokeWidth={3}
            dot={{ r: 4 }}
          />

          {/* DSM-V Marker */}
          <ReferenceLine
            x="2013"
            stroke="green"
            strokeDasharray="4 4"
            ifOverflow="visible"
          >
            <Label
              value="DSM-V (2013)"
              position="insideTop"
              fill="green"
              fontSize={12}
            />
          </ReferenceLine>
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>

  <div className="mt-4 text-sm text-gray-700">
    <strong>Key Insight:</strong> ASD prevalence in Utah has risen sharply from fewer than 1 per 1,000 in the late 1980s to nearly 27 per 1,000 by 2025. The 2013 DSM-V diagnostic changes coincide with a leveling trend, followed by continued gradual growth.
  </div>
</Card>




    <details className="text-md text-gray-500 mt-2  cursor-pointer">
  <summary className="hover:underline">Sources</summary>
  <ul className="list-disc list-inside ml-2">
    <li><a href="https://www.cdc.gov/mmwr/volumes/74/ss/ss7402a1.htm" target="_blank" rel="noreferrer">CDC MMWR</a></li>
    <li><a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC4467195/" target="_blank" rel="noreferrer">PMC Research</a></li>
    <li><a href="https://www.ncsautism.org/" target="_blank" rel="noreferrer">NCS Autism</a></li>
    <li><a href="https://healthcare.utah.edu/press-releases/2025/04/autism-prevalence-utah-remains-steady-lower-nation-report" target="_blank" rel="noreferrer">Utah Healthcare</a></li>
  </ul>
</details>


        {/* ========== KEEP all your original URADD charts here (sex, race, referral, wait time, ADDM) ========== */}
      </main>
     {/* <div className="text-xs text-gray-500 mt-2">
  Sources: 
  <a href="https://www.cdc.gov/mmwr/volumes/74/ss/ss7402a1.htm" target="_blank" rel="noreferrer" className="hover:underline ml-1">
    CDC
  </a>, 
  <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC4467195/" target="_blank" rel="noreferrer" className="hover:underline ml-1">
    PMC
  </a>, 
  <a href="https://www.ncsautism.org/" target="_blank" rel="noreferrer" className="hover:underline ml-1">
    NCS Autism
  </a>, 
  <a href="https://healthcare.utah.edu/press-releases/2025/04/autism-prevalence-utah-remains-steady-lower-nati…" target="_blank" rel="noreferrer" className="hover:underline ml-1">
    Utah Healthcare
  </a>
</div> */}





      <Footer />
    </div>
  );
}
